import {
  Callout,
  CheckboxVisibility,
  CommandBarButton,
  DirectionalHint,
  IColumn,
  Icon,
  IconButton,
  IDragDropEvents,
  Panel,
  PanelType,
  ShimmeredDetailsList,
  Toggle,
} from '@fluentui/react';
import { deletePatientInsurance, fetchPatientInsurances, updatePatientInsurancesCoverageOrder } from '../../../api/patients';
import { PatientInsurancePlan } from '../../../api/patients.types';
import { Badge } from '../../../components/core/Badge';
import { OkCancelDialog } from '../../../components/core/OkCancelDialog';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../../redux/message/actions';
import { ToastParams } from '../../../redux/message/types';
import { RootState } from '../../../redux/types';
import { css } from '../../../util/Css';
import { CreatePatientInsurancePlanForm } from './CreateInsurancePlanForm';
import styles from './PatientInsurancesTable.module.scss';

const COVERAGE_BADGES = [
  { content: 'PRIMARY', className: 'green-badge' },
  { content: 'SECONDARY', className: 'orange-badge' },
  { content: 'TERTIARY', className: 'blue-badge' },
];

interface Props {
  patientId: string;
  compact?: boolean;
  showControlsVisibilityToggle?: boolean; // A toggle to let users show or hide drag/drop and delete controls
  onUpdatedInsurances?: (patientId: string) => void;
}

export const PatientInsurancesTable = ({ patientId, compact = false, showControlsVisibilityToggle = false, onUpdatedInsurances }: Props) => {
  const [insurances, setInsurances] = useState<PatientInsurancePlan[]>([]);
  const [dragItemIndex, setDragItemIndex] = useState<number>();

  const [showPriorityToolTip, setShowPriorityToolTip] = useState(false);
  const [showAddInsurance, setShowAddInsurance] = useState(false);
  const [showTableLoading, setShowTableLoading] = useState(false);

  const [hideDragAndDeleteControls, setHideDragAndDeleteControls] = useState(showControlsVisibilityToggle);
  const dispatch = useDispatch();

  const dispatchToast = useCallback((params: ToastParams) => dispatch(toast(params)), [dispatch]);

  const payers = useSelector((state: RootState) => state.visits.visitConfig.data?.payers ?? []).filter((payer) => payer.availableToSelect);

  const canDrag = insurances.length > 1 && !hideDragAndDeleteControls;
  const getDragDropEvents: IDragDropEvents = {
    canDrop: () => true,
    canDrag: () => canDrag,
    onDragEnter: () => styles.dropCandidate,
    onDragStart: (_?: PatientInsurancePlan, itemIndex?: number) => setDragItemIndex(itemIndex),
    onDragEnd: () => setDragItemIndex(undefined),
    onDrop: (itemDroppedOn?: PatientInsurancePlan) => {
      const droppedOnSelf = dragItemIndex !== undefined && itemDroppedOn?.id === insurances?.[dragItemIndex].id;
      if (!itemDroppedOn || droppedOnSelf || dragItemIndex === undefined) {
        return;
      }

      const plan = insurances[dragItemIndex];
      const fromIndex = dragItemIndex;
      const toIndex = insurances.findIndex((plan) => plan.id === itemDroppedOn.id);

      updateInsurancesCoverageOrder(plan, fromIndex, toIndex);
    },
  };

  const getInsurances = useCallback(() => {
    setShowTableLoading(true);
    fetchPatientInsurances(patientId)
      .then(setInsurances)
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
      .finally(() => setShowTableLoading(false));
  }, [patientId]);

  useEffect(() => {
    getInsurances();
  }, [getInsurances]);

  async function updateInsurancesCoverageOrder(plan: PatientInsurancePlan, fromIndex: number, toIndex: number) {
    const insurancesToRestoreIfNeeded = cloneDeep(insurances);
    const clonedInsurances = cloneDeep(insurances);

    clonedInsurances.splice(fromIndex, 1);
    clonedInsurances.splice(toIndex, 0, plan);

    // Show the new order of insurances. If saving order fails, restore insurances to original state.
    setInsurances(clonedInsurances);

    setShowTableLoading(true);
    const insuranceIds = clonedInsurances.map((plan) => plan.id);
    updatePatientInsurancesCoverageOrder(patientId, insuranceIds)
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .then(() => onUpdatedInsurances && onUpdatedInsurances(patientId))
      .catch(() => setInsurances(insurancesToRestoreIfNeeded))
      .finally(() => setShowTableLoading(false));
  }

  const renderFlyout = () => {
    return (
      <Panel isOpen={showAddInsurance} onDismiss={() => setShowAddInsurance(false)} type={PanelType.smallFixedFar} headerText="Add Insurance Plan">
        <CreatePatientInsurancePlanForm
          payers={payers}
          patientId={patientId}
          onSuccess={() => {
            setShowAddInsurance(false);
            dispatchToast({ type: 'success', title: 'Success', content: 'Insurance plan was saved.' });
            onUpdatedInsurances && onUpdatedInsurances(patientId);
            getInsurances();
          }}
        />
      </Panel>
    );
  };

  const columns: IColumn[] = [
    {
      key: 'coverageOrder',
      name: 'Priority',
      minWidth: compact ? 90 : 120,
      maxWidth: compact ? 90 : 120,
      onRenderHeader: () => (
        <>
          Priority
          {!compact && <IconButton className="help-icon-button-target" iconProps={{ iconName: 'Info' }} onClick={() => setShowPriorityToolTip((prevState) => !prevState)} />}
        </>
      ),
      onRender: (_: PatientInsurancePlan, index?: number) => {
        const badge = index !== undefined && COVERAGE_BADGES?.[index];

        if (!badge) {
          return null;
        }

        return (
          <div style={{ display: 'flex', gap: 10 }}>
            {canDrag && <i className="icon ms-Icon ms-font-xl ms-Icon--GripperDotsVertical" />}
            <Badge style={{ width: 100, fontSize: 8 }} text={badge.content} className={css('label-as-badge', badge.className)} />
          </div>
        );
      },
    },
    {
      key: 'Payer',
      name: 'Payer',
      minWidth: 30,
      maxWidth: compact ? 100 : 180,
      isMultiline: true,
      onRender: (item: PatientInsurancePlan) => item.payer.name,
    },
    {
      key: 'Plan',
      name: 'Plan',
      minWidth: 30,
      maxWidth: compact ? 150 : 180,
      isMultiline: true,
      onRender: (item: PatientInsurancePlan) => item.planName,
    },
    {
      key: 'MemberID',
      name: 'Member ID',
      minWidth: 20,
      maxWidth: compact ? 150 : 198,
      isMultiline: true,
      onRender: (item: PatientInsurancePlan) => item.memberId,
    },
    {
      key: 'GroupId',
      name: 'Group ID',
      minWidth: 20,
      maxWidth: compact ? 150 : 198,
      isMultiline: true,
      onRender: (item: PatientInsurancePlan) => item.groupId,
    },
    {
      key: 'Status',
      name: 'Status',
      minWidth: 20,
      maxWidth: compact ? 150 : 198,
      isMultiline: true,
      onRender: (item: PatientInsurancePlan) => {
        return (
          <div>
            <div>{item.status}</div>
            <div>{item.statusUpdateDate ? 'Checked: ' + moment(item.statusUpdateDate).tz('America/Los_Angeles').format('MM/DD/YY h:mm A') : ''}</div>
          </div>
        );
      },
    },
  ];

  if (!compact) {
    columns.push({
      key: 'PlanStart',
      name: 'Plan Start',
      minWidth: 20,
      maxWidth: 100,
      onRender: (item: PatientInsurancePlan) => (item.planStart ? moment(item.planStart).format('MM/DD/YY') : '-'),
    });
    columns.push({
      key: 'PlanEnd',
      name: 'Plan End',
      minWidth: 20,
      maxWidth: 100,
      onRender: (item: PatientInsurancePlan) => (item.planEnd ? moment(item.planEnd).format('MM/DD/YY') : '-'),
    });
    if (!hideDragAndDeleteControls) {
      columns.push({
        key: 'remove',
        name: '',
        minWidth: 40,
        maxWidth: 40,
        fieldName: 'remove',
        onRender: (item: PatientInsurancePlan) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Icon iconName="Delete" className="action-icon" onClick={() => onClickRemoveInsurancePlan(item.id, item.planName)} />
          </div>
        ),
      });
    }
  }

  const PriorityToolTip = () => {
    return !showPriorityToolTip ? null : (
      <Callout directionalHint={DirectionalHint.bottomCenter} target=".help-icon-button-target" onDismiss={() => setShowPriorityToolTip(false)}>
        <div style={{ margin: 15, width: 400 }}>
          <p>Drag to reorder insurance plans.</p>
          <br />
          <p>
            The insurance plan positioned at the top is the <b>Primary</b> insurance plan. It is used first for processing claims. <br />
          </p>
          <br />
          As each insurance plan is used for processing claims, any remaining balance for claims will roll to the next insurance plan in the order listed here.
        </div>
      </Callout>
    );
  };

  const onClickRemoveInsurancePlan = (planId: string, planName: string) => {
    OkCancelDialog.show({
      title: 'Remove Insurance Plan',
      subText: `Are you sure you want to remove ${planName}?`,
      okText: 'Yes, remove plan',
      onOk: () =>
        deletePatientInsurance(patientId, planId)
          .then(() => {
            dispatchToast({ type: 'success', title: 'Success', content: 'Patient insurance plan was successfully removed.' });
            onUpdatedInsurances && onUpdatedInsurances(patientId);
            getInsurances();
          })
          .catch((err: Error) => dispatchToast({ type: 'error', title: 'Error', content: err.message })),
    });
  };

  return (
    <div style={{ paddingTop: compact ? 0 : undefined }}>
      <ShimmeredDetailsList
        items={insurances}
        columns={columns}
        checkboxVisibility={CheckboxVisibility.hidden}
        shimmerLines={3}
        enableShimmer={showTableLoading}
        selectionMode={0}
        dragDropEvents={getDragDropEvents}
        compact={compact}
      />
      {!insurances.length && <div style={{ textAlign: 'center', color: 'dark-gray' }}>No active insurance plans found for patient.</div>}
      <br />
      <div style={{ display: 'flex', justifyContent: 'space-between', height: 32 }}>
        <CommandBarButton iconProps={{ iconName: 'Add' }} text="Add new insurance plan" onClick={() => setShowAddInsurance(true)} data-tid="btn_addInsurance" />
        {showControlsVisibilityToggle && (
          <Toggle
            checked={!hideDragAndDeleteControls}
            label="Show edit insurances controls"
            inlineLabel
            styles={{ label: { fontWeight: 400 } }}
            onChange={(ev, checked) => setHideDragAndDeleteControls(!checked)}
          />
        )}
      </div>
      {renderFlyout()}
      <PriorityToolTip />
    </div>
  );
};
